import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ImagePlus, X } from 'lucide-react';
import { productsApi } from '../services/api';
import './ProductModal.css';

const ProductModal = ({ product, categories, selectedCategoryId, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: selectedCategoryId || null,
    price: '',
    has_custom_price: false,
    currency: '₪',
    is_active: true,
    in_stock: true,
    image_base64: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category_id: product.category_id || null,
        price: product.price || '',
        has_custom_price: product.has_custom_price || false,
        currency: product.currency || '₪',
        is_active: product.is_active !== false,
        in_stock: product.in_stock !== false,
        image_base64: null,
      });
      setImagePreview(product.image_url || null);
    }
  }, [product]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // If category changes and no custom price, use category default
      if (field === 'category_id' && !prev.has_custom_price) {
        const category = categories.find(c => c.id === value);
        if (category && category.default_price) {
          updated.price = category.default_price;
        }
      }
      
      // If user manually sets price, mark as custom
      if (field === 'price' && value !== '') {
        updated.has_custom_price = true;
      }
      
      return updated;
    });
  };

  const processImageFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('products.invalidImageType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('products.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setFormData(prev => ({ ...prev, image_base64: base64 }));
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    processImageFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    processImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError(t('products.nameRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const submitData = { ...formData };
      
      // Only include image_base64 if we have one
      if (!submitData.image_base64) {
        delete submitData.image_base64;
      }

      if (product) {
        await productsApi.update(product.id, submitData);
      } else {
        await productsApi.create(submitData);
      }
      
      onSave();
    } catch (err) {
      setError(err.message || t('products.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? t('products.editProduct') : t('products.addProduct')}</h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Image Upload */}
          <div className="form-group">
            <label>{t('products.image')}</label>
            <div
              className={`image-upload ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    className="remove-image"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagePreview(null);
                      setFormData(prev => ({ ...prev, image_base64: null }));
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <ImagePlus size={30} />
                  <p>{t('products.clickOrDrag')}</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Name */}
          <div className="form-group">
            <label>{t('products.name')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('products.namePlaceholder')}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>{t('products.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('products.descriptionPlaceholder')}
              rows="3"
            />
          </div>

          {/* Category */}
          <div className="form-group">
            <label>{t('products.category')}</label>
            <select
              value={formData.category_id || ''}
              onChange={(e) => handleChange('category_id', e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('products.noCategory')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div className="form-row">
            <div className="form-group">
              <label>{t('products.price')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>{t('products.currency')}</label>
              <select
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                <option value="₪">₪ (ILS)</option>
                <option value="$">$ (USD)</option>
                <option value="€">€ (EUR)</option>
              </select>
            </div>
          </div>

          {/* Custom Price Checkbox */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.has_custom_price}
                onChange={(e) => handleChange('has_custom_price', e.target.checked)}
              />
              <span>{t('products.customPrice')}</span>
            </label>
            <p className="help-text">{t('products.customPriceHelp')}</p>
          </div>

          {/* Status Checkboxes */}
          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span>{t('products.active')}</span>
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.in_stock}
                  onChange={(e) => handleChange('in_stock', e.target.checked)}
                />
                <span>{t('products.inStock')}</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
